import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import * as taskService from '../services/taskService';

export const useTaskManagement = ({
    activeTab,
    currentPage,
    searchTerm,
    statusFilter,
    priorityFilter,
    approvalStatusFilter,
    overdueFilter,
    taskTypeFilter,
    viewMode,
    groupBy,
    isManager,
    user
}) => {
    const { token } = useAuth();
    
    // State
    const [tasks, setTasks] = useState([]);
    const [groupedTasks, setGroupedTasks] = useState([]);
    const [users, setUsers] = useState({});
    const [stats, setStats] = useState(null);
    const [managerStats, setManagerStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedTask, setSelectedTask] = useState(null);
    const [subtasks, setSubtasks] = useState([]);
    const [subtasksLoading, setSubtasksLoading] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});
    // Task visibility scope, resolved server-side from Department.heads:
    // 'own' | 'department' | 'all'. The UI never derives this from permissions —
    // the backend is the single source of truth. Starts at the safest value.
    const [scope, setScope] = useState('own');

    // Fetch Tasks (List View)
    const fetchTasks = useCallback(async () => {
        if (!token) return;
        
        setLoading(true);
        try {
            let endpoint = '/api/tasks/my-tasks';
            
            if (activeTab === 'assigned-by-me') {
                endpoint = '/api/tasks/assigned-by-me';
            } else if (activeTab === 'all-tasks') {
                endpoint = '/api/tasks';
            }
            
            const params = { 
                page: currentPage, 
                limit: 10, 
                search: searchTerm, 
                status: Array.isArray(statusFilter) ? statusFilter.join(',') : statusFilter, 
                priority: Array.isArray(priorityFilter) ? priorityFilter.join(',') : priorityFilter,
                overdue: overdueFilter ? 'true' : '',
                taskType: taskTypeFilter || '',
                excludeSubtasks: 'true'
            };

            if (isManager && activeTab === 'assigned-to-me' && approvalStatusFilter) {
                params.approvalStatus = approvalStatusFilter;
            }

            const response = await taskService.fetchTasks(endpoint, params);
            if (response.success) {
                setTasks(response.data);
                setTotalPages(response.totalPages || 1);
                if (response.scope) setScope(response.scope);
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
        } finally {
            setLoading(false);
        }
    }, [token, activeTab, currentPage, searchTerm, statusFilter, priorityFilter, approvalStatusFilter, overdueFilter, taskTypeFilter, isManager]);

    // Fetch Grouped Tasks
    const fetchGroupedTasks = useCallback(async () => {
        if (!token) return;
        
        setLoading(true);
        try {
            const params = { 
                groupBy, 
                view: activeTab, 
                search: searchTerm, 
                status: Array.isArray(statusFilter) ? statusFilter.join(',') : statusFilter, 
                priority: Array.isArray(priorityFilter) ? priorityFilter.join(',') : priorityFilter,
                overdue: overdueFilter ? 'true' : '',
                taskType: taskTypeFilter || ''
            };

            const response = await taskService.fetchGroupedTasks(params);
            if (response.success) {
                setGroupedTasks(response.data);
                if (response.scope) setScope(response.scope);
                // Auto-expand all groups initially
                const expanded = {};
                response.data.forEach((group, idx) => {
                    expanded[idx] = true;
                });
                setExpandedGroups(expanded);
            }
        } catch (error) {
            console.error('Error fetching grouped tasks:', error);
        } finally {
            setLoading(false);
        }
    }, [token, groupBy, activeTab, searchTerm, statusFilter, priorityFilter, overdueFilter, taskTypeFilter]);

    // Fetch Users
    const fetchUsers = useCallback(async () => {
        if (!token) return;
        
        try {
            const response = await taskService.fetchUsers();
            if (response.success) {
                setUsers(response.data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, [token]);

    // Fetch Stats
    const fetchStats = useCallback(async () => {
        if (!token) return;
        
        try {
            if (isManager && activeTab === 'assigned-to-me') {
                const response = await taskService.fetchManagerApprovalStats();
                if (response.success) {
                    setManagerStats(response.data);
                    if (response.scope) setScope(response.scope);
                }
            } else {
                const response = await taskService.fetchDashboardStats(activeTab);
                if (response.success) {
                    setStats(response.data);
                    if (response.scope) setScope(response.scope);
                }
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, [token, isManager, activeTab]);

    // Fetch Task Details
    const fetchTaskDetails = useCallback(async (taskId) => {
        if (!token) return;
        
        try {
            const response = await taskService.fetchTaskDetails(taskId);
            if (response.success) setSelectedTask(response.data);
        } catch (error) {
            console.error('Error fetching task details:', error);
        }
    }, [token]);

    // Fetch Subtasks
    const fetchSubtasks = useCallback(async (parentId) => {
        if (!token) return;
        
        setSubtasksLoading(true);
        try {
            const response = await taskService.fetchSubtasks(parentId);
            if (response.success) setSubtasks(response.data || []);
        } catch (error) {
            console.error('Error fetching subtasks:', error);
            setSubtasks([]);
        } finally {
            setSubtasksLoading(false);
        }
    }, [token]);

    // Refresh data based on view mode
    const refreshTasks = useCallback(() => {
        if (viewMode === 'list') {
            fetchTasks();
        } else {
            fetchGroupedTasks();
        }
    }, [viewMode, fetchTasks, fetchGroupedTasks]);

    // Create Task
    const handleCreateTask = useCallback(async (taskData, attachmentFiles) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.createTask(taskData, attachmentFiles);
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create task' 
            };
        }
    }, [token, refreshTasks, fetchStats]);

    // Update Task Status
    const handleStatusChange = useCallback(async (taskId, newStatus) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.updateTaskStatus(taskId, newStatus);
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update status' 
            };
        }
    }, [token, refreshTasks, fetchStats]);

    // Delete Task
    const handleDeleteTask = useCallback(async (taskId) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.deleteTask(taskId);
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to delete task' 
            };
        }
    }, [token, refreshTasks, fetchStats]);

    // Add Comment
    const handleAddComment = useCallback(async (taskId, commentText) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.addComment(taskId, commentText);
            await fetchTaskDetails(taskId);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to add comment' 
            };
        }
    }, [token, fetchTaskDetails]);

    // Mark Comments as Read
    const handleMarkCommentsAsRead = useCallback(async (taskId) => {
        if (!token) return { success: false };
        
        try {
            await taskService.markCommentsAsRead(taskId);
            
            // Update the badge count locally
            if (viewMode === 'list') {
                setTasks(prevTasks => 
                    prevTasks.map(t => 
                        t._id === taskId 
                            ? { ...t, unreadCommentCount: 0 }
                            : t
                    )
                );
            } else {
                setGroupedTasks(prevGroupedTasks =>
                    prevGroupedTasks.map(group => ({
                        ...group,
                        tasks: group.tasks.map(t =>
                            t._id === taskId
                                ? { ...t, unreadCommentCount: 0 }
                                : t
                        )
                    }))
                );
            }
            return { success: true };
        } catch (error) {
            return { success: false };
        }
    }, [token, viewMode]);

    // Reassign Task
    const handleReassign = useCallback(async (taskId, assignedTo, reason) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.reassignTask(taskId, assignedTo, reason);
            await fetchTaskDetails(taskId);
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to reassign task' 
            };
        }
    }, [token, fetchTaskDetails, refreshTasks, fetchStats]);

    // Approve/Reject Task
    const handleApprovalAction = useCallback(async (taskId, action, remarks) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            if (action === 'approve') {
                await taskService.approveTask(taskId, remarks);
            } else {
                await taskService.rejectTask(taskId, remarks);
            }
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || `Failed to ${action} task` 
            };
        }
    }, [token, refreshTasks, fetchStats]);

    // Verify/Unverify Task
    const handleVerifyAction = useCallback(async (taskId, action, remarks) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            if (action === 'verify') {
                await taskService.verifyTask(taskId, remarks);
            } else {
                await taskService.unverifyTask(taskId, remarks);
            }
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || `Failed to ${action} task` 
            };
        }
    }, [token, refreshTasks, fetchStats]);

    // Update Task
    const handleUpdateTask = useCallback(async (taskId, updatedData) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.updateTask(taskId, updatedData);
            await fetchTaskDetails(taskId);
            refreshTasks();
            fetchStats();
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update task' 
            };
        }
    }, [token, fetchTaskDetails, refreshTasks, fetchStats]);

    // Create Subtask
    const handleCreateSubtask = useCallback(async (parentTaskId, subtaskData) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            const response = await taskService.createSubtask(parentTaskId, subtaskData);
            if (response.success) {
                await fetchSubtasks(parentTaskId);
                await fetchTaskDetails(parentTaskId);
                return { success: true };
            }
            return { success: false, message: 'Failed to create subtask' };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to create subtask' 
            };
        }
    }, [token, fetchSubtasks, fetchTaskDetails]);

    // Update Subtask Status
    const handleSubtaskStatusChange = useCallback(async (parentTaskId, subtaskId, status) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.updateSubtaskStatus(subtaskId, status);
            await fetchSubtasks(parentTaskId);
            await fetchTaskDetails(parentTaskId);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to update subtask status' 
            };
        }
    }, [token, fetchSubtasks, fetchTaskDetails]);

    // Add Task Connection
    const handleAddConnection = useCallback(async (taskId, linkedTaskId, type) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.addTaskConnection(taskId, linkedTaskId, type);
            await fetchTaskDetails(taskId);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to add link' 
            };
        }
    }, [token, fetchTaskDetails]);

    // Remove Task Connection
    const handleRemoveConnection = useCallback(async (taskId, connectionId) => {
        if (!token) return { success: false, message: 'Not authenticated' };
        
        try {
            await taskService.removeTaskConnection(taskId, connectionId);
            await fetchTaskDetails(taskId);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to remove link' 
            };
        }
    }, [token, fetchTaskDetails]);

    // Toggle Group Expansion
    const toggleGroup = useCallback((groupIndex) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupIndex]: !prev[groupIndex]
        }));
    }, []);

    // Initial data fetch
    useEffect(() => {
        if (viewMode === 'list') {
            fetchTasks();
        } else {
            fetchGroupedTasks();
        }
    }, [viewMode, fetchTasks, fetchGroupedTasks]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return {
        // State
        tasks,
        setTasks,
        groupedTasks,
        setGroupedTasks,
        users,
        stats,
        managerStats,
        loading,
        totalPages,
        selectedTask,
        setSelectedTask,
        subtasks,
        setSubtasks,
        subtasksLoading,
        expandedGroups,
        scope,

        // Actions
        fetchTasks,
        fetchGroupedTasks,
        fetchUsers,
        fetchStats,
        fetchTaskDetails,
        fetchSubtasks,
        refreshTasks,
        handleCreateTask,
        handleStatusChange,
        handleDeleteTask,
        handleAddComment,
        handleMarkCommentsAsRead,
        handleReassign,
        handleApprovalAction,
        handleVerifyAction,
        handleUpdateTask,
        handleCreateSubtask,
        handleSubtaskStatusChange,
        handleAddConnection,
        handleRemoveConnection,
        toggleGroup
    };
};
